import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Select from "react-select";
import { useLazyQuery, gql } from "@apollo/client";
import useGlobalMutation from "../../hooks/useGlobalMutation";
import { ADD_COURSE, GET_COURSE_IDS_FOR_COUNTRY } from "./query";
import { clearCache } from "../../utils";
import { FormContainer } from "../shared";
import LmsService from "../../utils/lmsService";
import { useDispatch, useSelector } from "react-redux";
import { setCourseList } from "../../reducers/lmsCourseList";

interface AddCourseToTierProps {
    tierId: string;
    tierCountry: string;
    onAssignSuccess?: () => void;
    onAssignCancel?: () => void;
}

interface CourseState {
    lmsCourseList: Course[];
}

interface Course {
    value: string;
    label: string;
}

const AddCourseToTier = ({
    tierId,
    tierCountry,
    onAssignSuccess,
    onAssignCancel,
}: AddCourseToTierProps) => {
    const dispatch = useDispatch();
    const courseList = useSelector((state: CourseState) => state.lmsCourseList);
    const [courseLoading, setCourseLoading] = useState(true);
    const selectRef = useRef<any>();

    useEffect(() => {
        const fetchCourseList = async () => {
            try {
                if (courseList.length === 0) {
                    const courses = await LmsService.getCourses();
                    dispatch(setCourseList(courses));
                    setCourseLoading(false);
                } else {
                    setCourseLoading(false);
                }
            } catch (error) {
                toast.error(error.message);
                setCourseLoading(false);
            }
        };

        fetchCourseList();
    }, [dispatch, courseList]);

    const [coursesAssignedForCountry, setCoursesAssignedForCountry] = useState<string[]>([]);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [getCourseIdsForCountry] = useLazyQuery(gql(GET_COURSE_IDS_FOR_COUNTRY));

    const getAvailableCourses = (): Array<{ value: string; label: string }> => {
        return courseList.filter(
            (course: { value: string; label: string }) =>
                !coursesAssignedForCountry.includes(course.value.toString())
        );
    };

    const fetchAndSetCourseIDsForCountry = async () => {
        try {
            const assignedCourses = await getCourseIdsForCountry({
                variables: { country: tierCountry },
            });

            setCoursesAssignedForCountry(
                Array.from(
                    new Set<string>(
                        assignedCourses.data.allAllerganProTierConfigurations.nodes.map(
                            (course: { courseId: string }) => course.courseId
                        )
                    )
                )
            );
            setFetchLoading(false);
        } catch (error) {
            toast.error(error.message);
            setFetchLoading(false);
        }
    };

    useEffect(() => {
        // resetCaches();
        fetchAndSetCourseIDsForCountry();
    }, [fetchLoading, getCourseIdsForCountry, tierCountry]);

    const {
        handleSubmit,
        formState: { errors },
        reset,
        control,
        setValue,
    } = useForm({
        mode: "onChange",
    });

    const { submitMutation, loading: submitLoading } = useGlobalMutation(
        ADD_COURSE,
        (cache: any) => {
            clearCache(cache, "allAllerganProTierConfigurations");
            clearCache(cache, "allAllerganProTiers");
        }
    );

    const submitAction = handleSubmit(async (data) => {
        const {
            courseInfo: { value: courseId, label: courseName },
        } = data;

        const allerganProTierConfiguration = {
            tierId,
            courseName,
            courseId: courseId.toString(),
        };

        const variables = {
            allerganProTierConfiguration,
        };

        const dataPath = "createAllerganProTierConfiguration.allerganProTierConfiguration";

        const isResultNull = await submitMutation(
            variables,
            dataPath,
            `Course assigned to the tier successfully`
        );

        onAssignSuccess?.();

        isResultNull === null && reset();

        selectRef?.current?.clearValue?.();
        setValue("courseInfo", null);
        fetchAndSetCourseIDsForCountry();
    });

    return (
        <FormContainer
            submitAction={submitAction}
            loading={fetchLoading || submitLoading || courseLoading}
            submitButtonText={"Add Course"}
            submitBtnSm={true}
            onFormCancel={onAssignCancel}
        >
            <div className="row">
                <div className="col-md-6">
                    <Controller
                        name="courseInfo"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <Select
                                {...field}
                                ref={selectRef}
                                options={getAvailableCourses()}
                                isClearable
                                placeholder="Select Course"
                                classNames={{
                                    control: () =>
                                        `h-100 form-control py-2 pr-0 pl-2 ${errors.courseInfo ? "is-invalid" : ""}`,
                                }}
                            />
                        )}
                    />
                </div>
            </div>
        </FormContainer>
    );
};

export default AddCourseToTier;
